import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useAuth } from '../contexts/AuthContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useEscalationContact } from '../contexts/EscalationContactContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { buildProxyIntakeTasks, escalationFor } from '../data/proxyWorkflowRegistry';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { listUsers, createUser, updateUser } from '../services/userService';
import type { ManagedUser } from '../types/api';
import { SearchInput } from '../components/SearchInput';
import {
  TEAMS, SCOPES, ROLES_BY_SCOPE, GUEST_PAGE_OPTIONS,
  bindingIds, scopeOf, teamLabel, roleLabel, type Scope,
} from '../utils/rbac';

const SCOPE_LABEL: Record<string, string> = {
  platform: 'Nexus', org: 'Organization', team: 'Team',
};

function scopeBadge(u: ManagedUser) {
  const s = scopeOf(u);
  const color = s === 'platform' ? 'bg-violet-100 text-violet-700'
    : s === 'org' ? 'bg-sky-100 text-sky-700'
    : 'bg-teal-100 text-teal-700';
  return <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${color}`}>{SCOPE_LABEL[s]}</span>;
}

const todayIso = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local

const isOutToday = (u: ManagedUser) =>
  !!u.out_from && !!u.out_until && u.out_from <= todayIso() && todayIso() <= u.out_until;

// '2026-10-05' -> 'Oct 5'
const formatDay = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Days an escalation has waited, against Proxy's demo "today".
function waitLabel(day: string): string {
  const d = Math.round((Date.parse(PROXY_SEED_TODAY) - Date.parse(day)) / 86400000);
  return d <= 0 ? 'today' : `${d}d`;
}

interface FormState {
  id: string | null;
  email: string;
  name: string;
  scope: Scope;
  role: string;
  teamId: string;
  pageAccess: string[];
  password: string;
  isActive: boolean;
  outFrom: string;
  outUntil: string;
  backupUserId: string;
}

const blankForm = (): FormState => ({
  id: null, email: '', name: '', scope: 'team', role: 'operator',
  teamId: TEAMS[0].id, pageAccess: [], password: '', isActive: true,
  outFrom: '', outUntil: '', backupUserId: '',
});

function fromUser(u: ManagedUser): FormState {
  const scope = scopeOf(u);
  return {
    id: u.id, email: u.username, name: u.name, scope,
    role: u.role, teamId: u.team_id ?? TEAMS[0].id,
    pageAccess: u.page_access ?? [], password: '', isActive: u.is_active,
    outFrom: u.out_from ?? '', outUntil: u.out_until ?? '', backupUserId: u.backup_user_id ?? '',
  };
}

function UserModal({ initial, users, onClose, onSaved }: {
  initial: FormState;
  users: ManagedUser[];
  onClose: () => void;
  onSaved: (u: ManagedUser) => void;
}) {
  const [f, setF] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = f.id !== null;
  // Out-of-office cover is for team managers: escalations to their team go
  // to the backup while they're out.
  const showCover = isEdit && f.scope === 'team' && f.role === 'manager';
  const backupOptions = users.filter(u => u.is_active && u.id !== f.id);

  const roleOptions = ROLES_BY_SCOPE[f.scope];
  // Keep role valid when scope changes.
  const setScope = (scope: Scope) =>
    setF(p => ({ ...p, scope, role: ROLES_BY_SCOPE[scope].includes(p.role) ? p.role : ROLES_BY_SCOPE[scope][0] }));

  const togglePage = (id: string) =>
    setF(p => ({ ...p, pageAccess: p.pageAccess.includes(id) ? p.pageAccess.filter(x => x !== id) : [...p.pageAccess, id] }));

  async function save() {
    setError(null);
    if (!isEdit && (!f.email.trim() || !f.password)) {
      setError('Email and password are required for a new user.');
      return;
    }
    if (showCover && (!f.outFrom !== !f.outUntil || (f.outFrom && f.outFrom > f.outUntil))) {
      setError('Out of office needs both dates, with the return date on or after the start.');
      return;
    }
    setSaving(true);
    const { org_id, team_id } = bindingIds(f.scope, f.teamId);
    const pageAccess = f.role === 'guest' ? f.pageAccess : null;
    try {
      const saved = isEdit
        ? await updateUser(f.id!, {
            name: f.name.trim(), role: f.role, org_id, team_id,
            page_access: pageAccess, is_active: f.isActive,
            password: f.password || undefined,
            ...(showCover ? {
              out_from: f.outFrom || null, out_until: f.outUntil || null, backup_user_id: f.backupUserId || null,
            } : {}),
          })
        : await createUser({
            email: f.email.trim(), name: f.name.trim() || f.email.trim(),
            password: f.password, role: f.role, org_id, team_id, page_access: pageAccess,
          });
      onSaved(saved);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not save user.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-modal w-[520px] max-h-[88vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-trim flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">{isEdit ? 'Edit user' : 'Add user'}</h3>
          <button onClick={onClose} className="text-muted hover:text-ink text-lg leading-none">×</button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted uppercase tracking-wide">Email</label>
              <input
                disabled={isEdit}
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1 disabled:bg-slate-50 disabled:text-muted"
                value={f.email}
                onChange={e => setF(p => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted uppercase tracking-wide">Name</label>
              <input
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                value={f.name}
                onChange={e => setF(p => ({ ...p, name: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted uppercase tracking-wide">Scope</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                value={f.scope}
                onChange={e => setScope(e.target.value as Scope)}
              >
                {SCOPES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted uppercase tracking-wide">Role</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                value={f.role}
                onChange={e => setF(p => ({ ...p, role: e.target.value }))}
              >
                {roleOptions.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
          </div>

          {f.scope === 'team' && (
            <div>
              <label className="text-xs font-semibold text-muted uppercase tracking-wide">Team</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                value={f.teamId}
                onChange={e => setF(p => ({ ...p, teamId: e.target.value }))}
              >
                {TEAMS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          )}

          {f.role === 'guest' && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-muted uppercase tracking-wide">Visible pages</label>
                <div className="flex items-center gap-2 text-xs font-medium">
                  <button type="button" onClick={() => setF(p => ({ ...p, pageAccess: GUEST_PAGE_OPTIONS.map(o => o.id) }))} className="text-teal-600 hover:text-teal-700">
                    Select all
                  </button>
                  <span className="text-muted">·</span>
                  <button type="button" onClick={() => setF(p => ({ ...p, pageAccess: [] }))} className="text-teal-600 hover:text-teal-700">
                    Select none
                  </button>
                </div>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                {GUEST_PAGE_OPTIONS.map(opt => (
                  <label key={opt.id} className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                    <input type="checkbox" checked={f.pageAccess.includes(opt.id)} onChange={() => togglePage(opt.id)} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-muted uppercase tracking-wide">
              {isEdit ? 'Reset password (optional)' : 'Password'}
            </label>
            <input
              type="password" autoComplete="new-password"
              className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
              value={f.password}
              onChange={e => setF(p => ({ ...p, password: e.target.value }))}
            />
          </div>

          {showCover && (
            <div className="rounded-lg border border-trim p-3 space-y-2">
              <div>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide">Out of office</div>
                <div className="text-xs text-muted mt-0.5">While they're out, escalations to their team go to the backup.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted">From</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                    value={f.outFrom}
                    onChange={e => setF(p => ({ ...p, outFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted">Back on</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                    value={f.outUntil}
                    onChange={e => setF(p => ({ ...p, outUntil: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted">Backup</label>
                <select
                  className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                  value={f.backupUserId}
                  onChange={e => setF(p => ({ ...p, backupUserId: e.target.value }))}
                >
                  <option value="">No backup</option>
                  {backupOptions.map(u => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
                </select>
              </div>
              {(f.outFrom || f.outUntil) && (
                <button type="button" onClick={() => setF(p => ({ ...p, outFrom: '', outUntil: '' }))} className="text-xs font-medium text-teal-600 hover:text-teal-700">
                  Clear dates
                </button>
              )}
            </div>
          )}

          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
              <input type="checkbox" checked={f.isActive} onChange={e => setF(p => ({ ...p, isActive: e.target.checked }))} />
              Active
            </label>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-trim flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm border border-trim text-copy hover:bg-white">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-lg disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UserManagementPage() {
  const { setTopbar } = useAppShell();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const { jobs } = useProxyData();
  const proxyCtx = useProxyWorkflowContext();
  const { contact, refresh: refreshContact } = useEscalationContact();
  useEffect(() => { setTopbar('Admin Dashboard', 'Roles, teams, and access'); }, [setTopbar]);

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState<FormState | null>(null);

  useEffect(() => {
    listUsers()
      .then(setUsers)
      .catch(() => setError('Could not load users.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q) || u.role.toLowerCase().includes(q));
  }, [users, query]);

  // Open Proxy escalations per person: escalated by the team to someone, or
  // sent up by Ace, which goes to whoever is covering for the manager today.
  const openEscalations = useMemo(() => {
    const byUser = new Map<string, { count: number; oldest: string }>();
    const routedId = contact?.routed_to.id;
    for (const task of buildProxyIntakeTasks(proxyCtx)) {
      const job = jobs.find(j => j.jobId === task.jobId);
      if (!job || !escalationFor(task, job, proxyCtx.emails)) continue;
      const uid = job.escalatedToUserId || routedId;
      if (!uid) continue;
      const day = task.createdAt.slice(0, 10);
      const cur = byUser.get(uid);
      byUser.set(uid, { count: (cur?.count ?? 0) + 1, oldest: cur && cur.oldest < day ? cur.oldest : day });
    }
    return byUser;
  }, [proxyCtx, jobs, contact]);

  const onSaved = (saved: ManagedUser) => {
    setUsers(prev => {
      const idx = prev.findIndex(u => u.id === saved.id);
      if (idx === -1) return [...prev, saved];
      const next = [...prev]; next[idx] = { ...prev[idx], ...saved }; return next;
    });
    refreshContact();
    setModal(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-8 py-4 flex items-center justify-between gap-4 border-b border-trim">
        <SearchInput value={query} onChange={setQuery} placeholder="Search users…" />
        <button
          onClick={() => setModal(blankForm())}
          className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-lg whitespace-nowrap"
        >
          + Add user
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-4">
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted border-b border-trim">
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">Email</th>
              <th className="px-3 py-2.5">Scope</th>
              <th className="px-3 py-2.5">Role</th>
              <th className="px-3 py-2.5">Team</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Escalations</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-wash hover:bg-[#FAFAFA] text-sm">
                <td className="px-3 py-2.5 font-medium text-ink">
                  {u.name}{me?.id === u.id && <span className="ml-1.5 text-xs text-muted">(you)</span>}
                </td>
                <td className="px-3 py-2.5 text-copy">{u.username}</td>
                <td className="px-3 py-2.5">{scopeBadge(u)}</td>
                <td className="px-3 py-2.5 text-ink">{roleLabel(u.role)}</td>
                <td className="px-3 py-2.5 text-copy">{u.team_id ? teamLabel(u.team_id) : '—'}</td>
                <td className="px-3 py-2.5">
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {isOutToday(u) && (
                    <span
                      className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 whitespace-nowrap"
                      title={u.backup_user_id ? `Backup: ${users.find(b => b.id === u.backup_user_id)?.name ?? 'unknown'}` : 'No backup set'}
                    >
                      Out until {formatDay(u.out_until!)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {openEscalations.get(u.id) ? (
                    <button onClick={() => navigate('/proxy/escalations')} className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap">
                      {openEscalations.get(u.id)!.count} open · oldest {waitLabel(openEscalations.get(u.id)!.oldest)}
                    </button>
                  ) : <span className="text-muted">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={() => setModal(fromUser(u))} className="text-sm text-teal-600 hover:text-teal-700 font-medium">Edit</button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-muted text-sm">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && <UserModal initial={modal} users={users} onClose={() => setModal(null)} onSaved={onSaved} />}
    </div>
  );
}
